import Link from 'next/link';

import { Panel } from './panel';
import { toSeasonLabel } from '@lib/page-utils/player-profile/filters';
import {
  formatNumber,
  formatOwnership,
  formatPrice
} from '@lib/page-utils/player-profile/format';

/** Below this, a difference is rounding noise rather than a real gap. */
const DELTA_EPSILON = 0.005;

export type SummaryLink = {
  label: string;
  href: string;
};

type SummaryBlockProps = {
  displayName: string;
  position: string;
  teamName: string;
  season: number;
  price: number | null;
  ownership: number | null;
  pointsPerGame: number | null;
  expectedPointsPerGame: number | null;
  comparisonLinks: SummaryLink[];
  clubmateLinks: SummaryLink[];
};

const PillLink = ({ link }: { link: SummaryLink }) => (
  <Link
    href={link.href}
    className="inline-flex min-h-[36px] items-center rounded-full border border-neutral-300 px-3 py-1.5 text-[13px] text-neutral-800 hover:border-neutral-400 hover:bg-neutral-100"
  >
    {link.label}
  </Link>
);

/**
 * Prose written from the same numbers rendered above it, so the copy cannot
 * drift from what the page shows. Every sentence is conditional on the value
 * existing — a player with no minutes gets a shorter summary, not a sentence
 * full of zeroes.
 */
const buildSummary = ({
  displayName,
  position,
  teamName,
  season,
  price,
  ownership,
  pointsPerGame,
  expectedPointsPerGame
}: Omit<SummaryBlockProps, 'comparisonLinks' | 'clubmateLinks'>): string[] => {
  const opening = [
    `${displayName} is a ${position} for ${teamName}`,
    price === null ? null : `priced at ${formatPrice(price)}`,
    ownership === null
      ? null
      : `and owned by ${formatOwnership(ownership)} of managers`
  ]
    .filter(Boolean)
    .join(', ');

  const paragraphs = [`${opening}.`];

  if (pointsPerGame !== null && expectedPointsPerGame !== null) {
    const difference = pointsPerGame - expectedPointsPerGame;
    // Rounding alone can land the two on the same figure, and calling that
    // "behind" would contradict the numbers printed directly above it.
    const isLevel = Math.abs(difference) < DELTA_EPSILON;
    const verdict = isLevel
      ? 'in line with what the underlying numbers expected'
      : difference > 0
      ? 'ahead of what the underlying numbers expected'
      : 'behind what the underlying numbers expected';

    paragraphs.push(
      `Across the ${toSeasonLabel(
        season
      )} season so far, ${displayName} has returned ${formatNumber(
        pointsPerGame,
        2
      )} points per appearance against an expected ${formatNumber(
        expectedPointsPerGame,
        2
      )}, putting them ${verdict}. Expected points account for chance quality rather than whether the chances were taken, so a gap in either direction tends to narrow over a full season.`
    );
  } else {
    paragraphs.push(
      `There is not yet enough match data for ${displayName} this season to compare returns against expected points. The gameweek log below fills in as appearances are recorded.`
    );
  }

  return paragraphs;
};

export const SummaryBlock = (properties: SummaryBlockProps) => {
  const { displayName, teamName, comparisonLinks, clubmateLinks } = properties;
  const paragraphs = buildSummary(properties);

  return (
    <Panel className="p-5 sm:p-6">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <h2 className="font-sans text-[17px] font-bold text-neutral-800">
            {displayName} FPL stats explained
          </h2>
          <div className="mt-3 flex flex-col gap-3">
            {paragraphs.map((paragraph) => (
              <p
                key={paragraph.slice(0, 40)}
                className="text-sm leading-[1.65] text-neutral-700 [text-wrap:pretty]"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-5">
          {comparisonLinks.length > 0 && (
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
                Compare with
              </h3>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {comparisonLinks.map((link) => (
                  <PillLink key={link.href} link={link} />
                ))}
              </div>
            </div>
          )}

          {clubmateLinks.length > 0 && (
            <div>
              <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-500">
                More {teamName} profiles
              </h3>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {clubmateLinks.map((link) => (
                  <PillLink key={link.href} link={link} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};
